import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { Auth } from '~/swagger/Auth';
import { Performance } from '~/swagger/Performance';
import { HttpClient } from '~/swagger/http-client';
import { Asset } from '~/swagger/Asset';
import { AssetMultipart } from '~/swagger/AssetMultipart';
import { Monitoring } from '~/swagger/Monitoring';
import { AuthProtocolGetInfoResponse, AuthProtocolLoginResponse } from '~/swagger/data-contracts';
import { Cr } from '~/swagger/Cr';
import { QueryKeyAuth } from '~/apis/auth';
import { queryClient } from '~/index';

const axiosConfig = {
  baseURL: process.env.REACT_APP_API_ROOT,
  timeout: 10000,
};

export const http: HttpClient = new HttpClient(axiosConfig);

export const authApi = new Auth(http);
export const customerRequestApi = new Cr(http);
export const assetApi = new Asset(http);
export const assetMultipartApi = new AssetMultipart(http);
export const monitoringApi = new Monitoring(http);
export const performanceApi = new Performance(http);

const UserInfoLocalStorageIdentifier = '_ctt_cli';

type CustomerViewData = { isClientView: boolean };

export const setAuth = (authData: AuthProtocolLoginResponse, isClientView?: boolean) => {
  queryClient.setQueryData([QueryKeyAuth], authData);
  if (isClientView) {
    sessionStorage.setItem(UserInfoLocalStorageIdentifier, JSON.stringify(authData));
  } else {
    localStorage.setItem(UserInfoLocalStorageIdentifier, JSON.stringify(authData));
  }
};

export const getAuth = (): (AuthProtocolLoginResponse & CustomerViewData) | null => {
  try {
    const { data, isClientView }: { data: string | null; isClientView: boolean } = (() => {
      const sessionAuth = sessionStorage.getItem(UserInfoLocalStorageIdentifier);
      if (sessionAuth) {
        return {
          data: sessionAuth,
          isClientView: true,
        };
      } else {
        return {
          data: localStorage.getItem(UserInfoLocalStorageIdentifier),
          isClientView: false,
        };
      }
    })();
    if (data) {
      const parseAuth = JSON.parse(data);

      return {
        ...parseAuth,
        isClientView,
      };
    }
    return null;
  } catch (e) {
    clearAuth();
    throw new Error('Failed to load user info');
  }
};

export const useAuth = (): UseQueryResult<AuthProtocolGetInfoResponse & CustomerViewData> =>
  useQuery([QueryKeyAuth], getAuth);

export const clearAuth = (): void => {
  queryClient.setQueryData([QueryKeyAuth], null);
  queryClient.clear();
  if (sessionStorage.getItem(UserInfoLocalStorageIdentifier)) {
    sessionStorage.removeItem(UserInfoLocalStorageIdentifier);
  } else {
    localStorage.removeItem(UserInfoLocalStorageIdentifier);
  }
};

(function initializeToErrorHandling() {
  const requestWaitQueue: ((token: string) => void)[] = [];
  let isInProgressRefreshToken = false;

  function report(message: string, unknown?: boolean) {
    // TODO
  }

  async function updateToken() {
    isInProgressRefreshToken = true;

    const data = queryClient.getQueryData<AuthProtocolLoginResponse>([QueryKeyAuth]);
    const refreshToken = data?.refreshToken;

    try {
      if (refreshToken) {
        const { data: response } = await authApi.refreshTokenCreate({ refreshToken: refreshToken });

        const authData: AuthProtocolLoginResponse = {
          accessToken: response.accessToken,
          refreshToken: refreshToken,
        };
        setAuth(authData);

        return response.accessToken;
      } else {
        throw new Error('refresh token is not exist.');
      }
    } catch (e) {
      clearAuth();
      window.location.href = '/';
    } finally {
      isInProgressRefreshToken = false;
    }
  }

  http.instance.interceptors.response.use(undefined, async (error) => {
    if (!error || !error.response) {
      report(error.message);
      return;
    }

    const status = error.response.status;
    const message = error.response.data ? error.response.data.error : 'An error occurred, Please try again later.';

    if (error.config && status === 401) {
      const { config: requestConfigToRetry } = error;

      if (!isInProgressRefreshToken) {
        updateToken().then((accessToken) => {
          while (accessToken && requestWaitQueue.length > 0) {
            const waitingRequest = requestWaitQueue.shift();

            if (waitingRequest) {
              waitingRequest(accessToken);
            }
          }
        });
      }

      return new Promise((resolve) => {
        requestWaitQueue.push((newAccessToken) => {
          requestConfigToRetry['headers'] = {
            ...requestConfigToRetry.headers,
            Authorization: `Bearer ${newAccessToken}`,
          };

          resolve(http.instance.request(requestConfigToRetry));
        });
      });
    } else if (status >= 500 || status === 422) {
      // Major Error
      report(message, true);
    } else {
      report(message);
    }

    return Promise.reject(error);
  });
})();

http.instance.interceptors.request.use((config) => {
  const authData = getAuth();
  if (authData?.accessToken && config?.headers) {
    config.headers['Authorization'] = `Bearer ${authData.accessToken}`;
  }
  return config;
});
